import { Box, Dialog, Grid, makeStyles } from '@material-ui/core'
import React, { useState, useEffect } from 'react'
import TextField from '@material-ui/core/TextField'
import { useDispatch, useSelector } from 'react-redux'
import { API_STATUS } from '../../../features/API/ApiHandler'
import { auth_config } from '../../../features/API/auth_config'
import Alert from '@material-ui/lab/Alert'
import { SET_METADATA } from '../../../features/State Management/questionnaireMetadataSlice'
import { GENERATE_INITIAL_QUESTIONNAIRE_METADATA_CONTEXT } from '../../../utils'
import { useSession } from '../../Organisms/Authentication Dialog/SessionProvider'
import DialogHeader from './DialogHeader'
import DialogFooter from './DialogFooter'
import useQuestionnaireMutation from '../../../customHooks/useQuestionnaireMutation'
import { LiveUpdateDialog } from '../LiveUpdateDialog'

export const useStyles = makeStyles((theme) => ({
  body: {
    padding: theme.spacing(2),
    '&>*': {
      padding: theme.spacing(4),
    },
  },
  button1: {
    height: '60px',
    width: '100%',
    borderRadius: 0,
  },
  button2: {
    height: '60px',
    width: '100%',
    borderRadius: 0,
  },
  stickyTop: {
    background: theme.palette.background.paper,
    position: 'sticky',
    top: 0,
    zIndex: 2,
  },
  stickyBot: {
    background: theme.palette.background.paper,
    position: 'sticky',
    bottom: 0,
    zIndex: 2,
  },
  mtb: {
    marginTop: theme.spacing(2),
  },
  header: {
    padding: theme.spacing(2),
    paddingLeft: theme.spacing(4),
  },
  required: {
    marginBottom: theme.spacing(4),
  },
  headerGrid: {
    padding: theme.spacing(2),
  },
}))

export const SaveQuestionnaireDialog = ({ open, setOpen }) => {
  const classes = useStyles()
  const metadata = useSelector((stateTwo) => stateTwo.questionnaireMetadata)
  const questions = useSelector((stateThree) => stateThree.questions)
  const [name, setName] = useState(metadata.name)
  const [title, setTitle] = useState(metadata.title)
  const [key, setKey] = useState(metadata.key)
  const [state, setState] = useState({ status: API_STATUS.INIT, body: null })
  const dispatch = useDispatch()
  const { mutateAsync: saveQuestionnaire } = useQuestionnaireMutation()
  const [liveWarningOpen, setLiveWarningOpen] = useState(false)

  const isNewQuestionnaire = !metadata?.key

  const { jwt, isAuthenticated } = useSession()

  useEffect(() => {
    if (isNewQuestionnaire) {
      const localMetadata = GENERATE_INITIAL_QUESTIONNAIRE_METADATA_CONTEXT()
      setName(localMetadata.name)
      setTitle(localMetadata.title)
      setKey(localMetadata.key)
    }
  }, [isNewQuestionnaire])

  // Note: no need to add isNewQuestionnaires to the dependency array since
  // it is derived from metadata
  useEffect(() => {
    if (!isNewQuestionnaire) {
      setName(metadata.name)
      setKey(metadata.key)
      setTitle(metadata.title)
    }
  }, [metadata]) // eslint-disable-line react-hooks/exhaustive-deps

  const handleClose = () => {
    setOpen(false)
    resetRequestState()
  }
  const resetRequestState = () => {
    setState({ status: API_STATUS.INIT, body: null })
  }

  const callCreateQuestionnaire = (questionnaire) => {
    if (!isAuthenticated) {
      setState({ status: API_STATUS.NOT_AUTHENTICATED, body: null })
    }

    setState({ status: API_STATUS.LOADING, body: null })
    const unirest = require('unirest')

    return unirest('POST', auth_config.base + '/api/v1/questionnaire')
      .headers({
        Authorization: `Bearer ${jwt}`,
        'Content-Type': 'application/json',
      })
      .send(JSON.stringify({ questionnaire: questionnaire }))
      .end(function (res) {
        if (res.error && isNetworkErrorMessage(res.error.message)) {
          setState({
            status: API_STATUS.ERROR,
            body: { special: 'The server cannot be reached.' },
          })
          return
        }
        responseHandlers[res.code](res)
      })
  }
  const errorInAttribute = (attributeName) =>
    state.status === API_STATUS.ERROR &&
    state.body.hasOwnProperty(attributeName)

  const handleBadRequest = (response) => {
    setState({ status: API_STATUS.ERROR, body: response.body.result })
  }

  const handleNotFound = (response) => {
    setState({
      status: API_STATUS.ERROR,
      body: { special: 'The questionnaire could not be found' },
    })
  }

  const handleSuccess = (response) => {
    // If the request succeeded the metadata could have potentially changed for
    // 2 reasons:
    // 1) the user typed in a new title.
    // 2) The user saved as a copy, which autogenerates new values for the key
    //    and name.
    // We can get the values accepted by the server from the dto of the request

    const dto = JSON.parse(response.request.body).questionnaire
    const newMetadata = {
      key: dto.key,
      name: dto.name,
      title: dto.title,
      live: metadata.live, // Note that this is unaffected by creating a questionnaire
    }

    dispatch(
      SET_METADATA({
        metadata: newMetadata,
      })
    )
    setState({ status: API_STATUS.IDLE, body: response.body.result })
  }
  const handleForbidden = (response) => {
    setState({
      status: API_STATUS.ERROR,
      body: { special: 'You need to be an admin to use this feature.' },
    })
  }
  const responseHandlers = {
    400: handleBadRequest,
    201: handleSuccess,
    403: handleForbidden,
  }

  const questionnaireDto = {
    name: name,
    content: {
      questions: questions,
      scores: [],
    },
    key: key,
    title: title,
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    let dto = questionnaireDto

    if (!isNewQuestionnaire) {
      const { key: newKey, name: newName } =
        GENERATE_INITIAL_QUESTIONNAIRE_METADATA_CONTEXT()
      dto = {
        ...questionnaireDto,
        key: newKey,
        name: newName,
      }
    }

    callCreateQuestionnaire(dto)
  }

  const generateAttributeTextField = (
    attributeName,
    attributeSetter,
    attributeState,
    disabled
  ) => {
    return (
      <TextField
        fullWidth
        required
        variant='filled'
        id='filled-basic'
        label={attributeName}
        value={attributeState}
        error={errorInAttribute(attributeName)}
        disabled={disabled}
        helperText={
          errorInAttribute(attributeName) ? state.body[attributeName] : null
        }
        onChange={(e) => {
          attributeSetter(e.target.value)
        }}
      />
    )
  }
  const generateStatusFeedback = () => {
    if (
      state.status === API_STATUS.ERROR &&
      state.body.hasOwnProperty('special')
    ) {
      return <Alert severity='error'>{state.body.special}</Alert>
    }

    if (state.status === API_STATUS.IDLE) {
      return (
        <Alert severity='success'>Your questionnaire has been saved!</Alert>
      )
    }
  }

  /**
   * Generates a unique identifier for a question based on its index and title.
   * @param {object} q - The question object.
   * @param {number} questionIdx - The index of the question in the list.
   * @param {number} subquestionIdx - The index of the sub question
   * @returns {string} - A unique identifier for the question.
   */
  const parseTitleToId = (q, questionIdx, subquestionIdx = undefined) => {
    // The title can be a string or an object with multiple languages.
    let originalTitle = ''
    if (typeof q.title === 'string') {
      originalTitle = q.title
    } else if (typeof q.title === 'object') {
      const languages = ['en', 'nl', 'i18n']
      let foundLanguage = false
      for (const language of languages) {
        if (q.title[language] && typeof q.title[language] === 'string') {
          originalTitle = q.title[language]
          foundLanguage = true
          break
        }
      }
      if (!foundLanguage) {
        originalTitle = JSON.stringify(q.title)
      }
    }
    // Replace spaces with underscores and remove non-alphabetic characters from the title.
    const sanitizedTitle = originalTitle
      .trim()
      .replaceAll(' ', '_')
      .replace(/[^0-9a-zA-Z_]/g, '')

    const subquestionString =
      typeof subquestionIdx !== 'undefined' ? `_${subquestionIdx}` : ''
    // Combine the index and the sanitized title to form a unique ID.
    return `v${questionIdx}${subquestionString}_${sanitizedTitle}`
  }

  const callSaveQuestionnaire = () => {
    setState({ status: API_STATUS.LOADING, body: null })
    let dto = questionnaireDto
    let updatedQuestions = dto.content.questions.map((q, i) => {
      if (q.type === 'expandable') {
        let updatedContent = q.content.map((subQuestion, j) => {
          return {
            ...subQuestion,
            id: parseTitleToId(subQuestion, i + 1, j + 1),
          }
        })
        q = { ...q, content: updatedContent }
      }
      if (q.type !== 'raw') {
        q = { ...q, id: parseTitleToId(q, i + 1) }
      }
      return q
    })

    // Update the dto with the modified questions
    dto = { ...dto, content: { ...dto.content, questions: updatedQuestions } }

    saveQuestionnaire(dto)
      .then((res) => {
        setState({ status: API_STATUS.IDLE, body: res.data.result })
        // make effective any potential changes in the metadata (e.g. title) to
        // the global metadata store
        dispatch(
          SET_METADATA({
            metadata: {
              key: dto.key,
              name: dto.name,
              title: dto.title,
              live: metadata.live, // Note that this is unaffected by saving a questionnaire
            },
          })
        )
      })
      .catch((error) => {
        if (error && isNetworkErrorMessage(error.message)) {
          setState({
            status: API_STATUS.ERROR,
            body: { special: 'The server cannot be reached.' },
          })
          return
        }
        const res = error.response
        if (res) {
          if (res.status === 400) {
            handleBadRequest({ body: res.data })
          } else if (res.status === 404) {
            handleNotFound(res)
          } else if (res.code === 403) {
            handleForbidden(res)
          }
        }
      })
  }

  const handleSave = () => {
    // We reset the save feedback to avoid confusing the user in case the
    // feedback was already there, the user clicked on save, and then clicked
    // on no in the live update warning dialog
    resetRequestState()
    // Pop a warning if trying to update a questionnaire that has responses
    // already
    if (metadata.live) {
      return setLiveWarningOpen(true)
    }

    callSaveQuestionnaire()
  }

  const handleYesToLiveUpdate = () => {
    setLiveWarningOpen(false)
    callSaveQuestionnaire()
  }

  return (
    <>
      {liveWarningOpen && (
        <LiveUpdateDialog
          open
          onClose={() => setLiveWarningOpen(false)}
          onYes={handleYesToLiveUpdate}
        />
      )}
      {!liveWarningOpen && (
        <Dialog open={open} onClose={handleClose} fullWidth maxWidth='md'>
          <form onSubmit={(e) => handleSubmit(e)}>
            <Grid
              container
              direction='column'
              justifyContent='center'
              alignItems='stretch'
            >
              <DialogHeader
                className={classes.header}
                state={state}
                isNewQuestionnaire={isNewQuestionnaire}
              />
              <Box p={2.5}>
                {generateAttributeTextField(
                  'name',
                  setName,
                  name,
                  !isNewQuestionnaire
                )}
                {generateAttributeTextField(
                  'key',
                  setKey,
                  key,
                  !isNewQuestionnaire
                )}
                {generateAttributeTextField('title', setTitle, title, false)}
                {generateStatusFeedback()}
              </Box>
              <DialogFooter
                handleClose={handleClose}
                state={state}
                isAuthenticated={isAuthenticated}
                isNewQuestionnaire={isNewQuestionnaire}
                onSave={handleSave}
              />
            </Grid>
          </form>
        </Dialog>
      )}
    </>
  )
}

const isNetworkErrorMessage = (errorMessage) => {
  return (
    errorMessage.includes('NetworkError') ||
    errorMessage.includes('Network Error') ||
    errorMessage.includes('Failed to fetch')
  )
}
